// Browser-native printing utilities — gives perfect Arabic RTL rendering,
// unlike jsPDF which does not embed Arabic glyphs by default.

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const printCss = `
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: 'Cairo', 'Segoe UI', 'Tahoma', Arial, sans-serif;
    color: #111; background: #fff;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .doc { padding: 8px; }
  .header {
    display: flex; align-items: center; gap: 16px;
    border-bottom: 2px solid #111; padding-bottom: 14px; margin-bottom: 20px;
  }
  .header img { width: 72px; height: 72px; object-fit: cover; border: 1px solid #ddd; }
  .header .title { flex: 1; }
  .header h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
  .header .sub { margin-top: 2px; font-size: 12px; color: #555; letter-spacing: 0.05em; }
  .header .date { font-size: 11px; color: #777; }
  h2 { font-size: 18px; margin: 20px 0 10px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; margin-bottom: 18px; }
  .cell { border-bottom: 1px solid #eee; padding: 8px 4px; }
  .cell .lbl { font-size: 10px; color: #777; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 2px; }
  .cell .val { font-size: 15px; font-weight: 600; color: #111; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  table th, table td { border-bottom: 1px solid #ddd; padding: 8px 6px; text-align: start; }
  table th { background: #f5f5f5; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #333; }
  tr:nth-child(even) td { background: #fafafa; }
  .foot { margin-top: 40px; padding-top: 20px; border-top: 1px dashed #bbb; display: flex; justify-content: space-between; font-size: 11px; color: #555; }
  .sign-line { border-top: 1px solid #333; width: 200px; margin-top: 40px; padding-top: 6px; text-align: center; font-size: 11px; color: #333; }
  .badge { display: inline-block; padding: 2px 8px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; border: 1px solid #333; }
  .badge.red { color: #B91C1C; border-color: #B91C1C; }
  .badge.yellow { color: #A16207; border-color: #A16207; }
  .badge.green { color: #166534; border-color: #166534; }
  .row-status { font-weight: 700; }
  @media print {
    body { -webkit-print-color-adjust: exact; }
    .no-print { display: none !important; }
  }
`;

const openPrintWindow = (title, dir, bodyHtml) => {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    alert("Please allow pop-ups to print.");
    return;
  }
  w.document.open();
  w.document.write(`<!doctype html>
<html dir="${dir}" lang="${dir === "rtl" ? "ar" : "en"}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />
  <style>${printCss}</style>
</head>
<body>
${bodyHtml}
<script>
  window.addEventListener('load', function () {
    setTimeout(function() { window.focus(); window.print(); }, 350);
  });
</script>
</body></html>`);
  w.document.close();
};

const buildHeader = (user, subtitle, lang) => {
  const officeName = user?.office_name || (lang === "ar" ? "متتبع الأسطول" : "FleetOil Tracker");
  const logo = user?.office_logo;
  const dateStr = new Date().toLocaleString(lang === "ar" ? "ar-EG" : "en-US");
  return `
    <div class="header">
      ${logo ? `<img src="${escapeHtml(logo)}" alt="logo" />` : ""}
      <div class="title">
        <h1>${escapeHtml(officeName)}</h1>
        <div class="sub">${escapeHtml(subtitle)}</div>
      </div>
      <div class="date">${escapeHtml(dateStr)}</div>
    </div>
  `;
};

const buildFooter = (lang) => {
  const rights = lang === "ar"
    ? "جميع الحقوق محفوظة للمهندس سلطان عبدالعزيز أبوذيبة"
    : "All rights reserved to Engineer Sultan Abdulaziz Aboudheba";
  return `<div class="foot">
    <div>${escapeHtml(rights)}</div>
    <div>${new Date().getFullYear()}</div>
  </div>`;
};

const fmt = (n, lang) => (n ?? 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-US");

// ---------- Public API ----------

export const printTripReceipt = ({ trip, car, user, lang = "ar" }) => {
  const t = lang === "ar"
    ? {
        subtitle: "إيصال إرجاع سيارة",
        plate: "رقم اللوحة",
        vehicle: "السيارة",
        renter: "اسم المستأجر",
        date: "تاريخ الإرجاع",
        returnKm: "كيلومتر الإرجاع",
        kmDriven: "الكيلومترات المقطوعة",
        currentKm: "الكيلومتر الحالي",
        remainingOil: "المتبقي حتى تبديل الزيت",
        notes: "ملاحظات",
        signRenter: "توقيع المستأجر",
        signOffice: "توقيع المكتب",
        km: "كم",
        tripId: "رقم الإيصال",
      }
    : {
        subtitle: "Vehicle Return Receipt",
        plate: "Plate number",
        vehicle: "Vehicle",
        renter: "Renter",
        date: "Return date",
        returnKm: "Return kilometers",
        kmDriven: "Kilometers driven",
        currentKm: "Current km",
        remainingOil: "Km until oil change",
        notes: "Notes",
        signRenter: "Renter signature",
        signOffice: "Office signature",
        km: "km",
        tripId: "Receipt ID",
      };

  const kmSinceOil = (car.current_km || 0) - (car.last_oil_change_km || 0);
  const remaining = (car.oil_change_interval_km || 0) - kmSinceOil;
  const level = remaining <= 0 ? "red" : (kmSinceOil / (car.oil_change_interval_km || 1) >= 0.8 ? "yellow" : "green");

  const body = `<div class="doc">
    ${buildHeader(user, t.subtitle, lang)}
    <div class="grid">
      <div class="cell"><div class="lbl">${t.tripId}</div><div class="val">${escapeHtml(trip.trip_id)}</div></div>
      <div class="cell"><div class="lbl">${t.date}</div><div class="val">${escapeHtml(new Date(trip.return_date).toLocaleString(lang === "ar" ? "ar-EG" : "en-US"))}</div></div>
      <div class="cell"><div class="lbl">${t.plate}</div><div class="val">${escapeHtml(car.plate_number)}</div></div>
      <div class="cell"><div class="lbl">${t.vehicle}</div><div class="val">${escapeHtml(car.make)} ${escapeHtml(car.model)} ${car.year ? `· ${car.year}` : ""}</div></div>
      <div class="cell"><div class="lbl">${t.renter}</div><div class="val">${escapeHtml(trip.renter_name || "—")}</div></div>
      <div class="cell"><div class="lbl">${t.currentKm}</div><div class="val">${fmt(trip.return_km, lang)} ${t.km}</div></div>
      <div class="cell"><div class="lbl">${t.kmDriven}</div><div class="val">+${fmt(trip.km_driven, lang)} ${t.km}</div></div>
      <div class="cell"><div class="lbl">${t.remainingOil}</div><div class="val"><span class="badge ${level}">${fmt(remaining.toFixed(0), lang)} ${t.km}</span></div></div>
    </div>
    ${trip.notes ? `
      <div class="cell" style="margin-bottom: 30px;">
        <div class="lbl">${t.notes}</div>
        <div class="val" style="font-weight: 400;">${escapeHtml(trip.notes)}</div>
      </div>
    ` : ""}
    <div style="display: flex; justify-content: space-between; gap: 40px; margin-top: 60px;">
      <div class="sign-line">${t.signRenter}</div>
      <div class="sign-line">${t.signOffice}</div>
    </div>
    ${buildFooter(lang)}
  </div>`;
  openPrintWindow(`${car.plate_number} - ${t.subtitle}`, lang === "ar" ? "rtl" : "ltr", body);
};

export const printFleetReport = ({ cars, user, lang = "ar" }) => {
  const t = lang === "ar"
    ? { subtitle: "تقرير أسطول السيارات", plate: "اللوحة", make: "الصانع", model: "الموديل", year: "السنة", currentKm: "الكيلومتر الحالي", lastOilKm: "آخر تبديل زيت", interval: "الفاصل", remaining: "المتبقي", status: "الحالة", km: "كم" }
    : { subtitle: "Fleet Oil-Change Report", plate: "Plate", make: "Make", model: "Model", year: "Year", currentKm: "Current KM", lastOilKm: "Last Oil KM", interval: "Interval", remaining: "Remaining", status: "Status", km: "km" };

  const rows = cars.map((c) => {
    const kmSince = (c.current_km || 0) - (c.last_oil_change_km || 0);
    const remaining = (c.oil_change_interval_km || 0) - kmSince;
    const pct = kmSince / (c.oil_change_interval_km || 1);
    const level = remaining <= 0 || pct >= 1 ? "red" : pct >= 0.8 ? "yellow" : "green";
    const label = level === "red" ? (lang === "ar" ? "عاجل" : "URGENT") : level === "yellow" ? (lang === "ar" ? "قريب" : "SOON") : (lang === "ar" ? "جيد" : "OK");
    return `<tr>
      <td>${escapeHtml(c.plate_number)}</td>
      <td>${escapeHtml(c.make)}</td>
      <td>${escapeHtml(c.model)}</td>
      <td>${c.year || "—"}</td>
      <td>${fmt(c.current_km, lang)}</td>
      <td>${fmt(c.last_oil_change_km, lang)}</td>
      <td>${fmt(c.oil_change_interval_km, lang)}</td>
      <td class="row-status"><span class="badge ${level}">${fmt(remaining.toFixed(0), lang)} ${t.km}</span></td>
      <td><span class="badge ${level}">${label}</span></td>
    </tr>`;
  }).join("");

  const body = `<div class="doc">
    ${buildHeader(user, t.subtitle, lang)}
    <table>
      <thead>
        <tr>
          <th>${t.plate}</th><th>${t.make}</th><th>${t.model}</th><th>${t.year}</th>
          <th>${t.currentKm}</th><th>${t.lastOilKm}</th><th>${t.interval}</th>
          <th>${t.remaining}</th><th>${t.status}</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="9" style="text-align:center;color:#888;padding:20px;">—</td></tr>`}</tbody>
    </table>
    ${buildFooter(lang)}
  </div>`;
  openPrintWindow(t.subtitle, lang === "ar" ? "rtl" : "ltr", body);
};

export const printTripsReport = ({ trips, cars, user, lang = "ar" }) => {
  const t = lang === "ar"
    ? { subtitle: "سجل إرجاعات السيارات", date: "التاريخ", plate: "اللوحة", vehicle: "السيارة", renter: "المستأجر", returnKm: "كم الإرجاع", kmDriven: "الكم المقطوع", notes: "ملاحظات" }
    : { subtitle: "Returns History Report", date: "Date", plate: "Plate", vehicle: "Vehicle", renter: "Renter", returnKm: "Return KM", kmDriven: "Km Driven", notes: "Notes" };
  const carMap = Object.fromEntries(cars.map((c) => [c.car_id, c]));
  const rows = trips.map((tr) => {
    const c = carMap[tr.car_id];
    return `<tr>
      <td>${escapeHtml(new Date(tr.return_date).toLocaleString(lang === "ar" ? "ar-EG" : "en-US"))}</td>
      <td>${escapeHtml(c?.plate_number || "")}</td>
      <td>${escapeHtml(c ? `${c.make} ${c.model}` : "")}</td>
      <td>${escapeHtml(tr.renter_name || "—")}</td>
      <td>${fmt(tr.return_km, lang)}</td>
      <td>+${fmt(tr.km_driven, lang)}</td>
      <td>${escapeHtml(tr.notes || "—")}</td>
    </tr>`;
  }).join("");

  const body = `<div class="doc">
    ${buildHeader(user, t.subtitle, lang)}
    <table>
      <thead>
        <tr>
          <th>${t.date}</th><th>${t.plate}</th><th>${t.vehicle}</th><th>${t.renter}</th>
          <th>${t.returnKm}</th><th>${t.kmDriven}</th><th>${t.notes}</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="7" style="text-align:center;color:#888;padding:20px;">—</td></tr>`}</tbody>
    </table>
    ${buildFooter(lang)}
  </div>`;
  openPrintWindow(t.subtitle, lang === "ar" ? "rtl" : "ltr", body);
};
